'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { NavItem } from '@/types/nav';
import { cn } from '@/lib/utils';

import { Icons } from './icons';

interface MainNavProps {
    items?: NavItem[];
}

export function MainNav({ items }: MainNavProps) {
    const pathname = usePathname();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    return (
        <div className="flex items-center justify-between w-full">
            {/* Logo */}
            <Link href="/" className="flex items-center space-x-2">
                <Image
                    src="/logo.png"
                    alt="Logo"
                    width={140}
                    height={40}
                    className="object-contain"
                />
            </Link>

            {/* Mobile Menu Button */}
            <button
                className="md:hidden p-2 text-gray-700 dark:text-gray-300"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
            >
                {mobileMenuOpen ? <Icons.x /> : <Icons.menu />}
            </button>

            {/* Desktop Navigation Links */}
            {items?.length ? (
                <nav className="hidden md:flex gap-6">
                    {items.map((item, index) =>
                        item.href ? (
                            <Link
                                key={index}
                                href={item.href}
                                className={cn(
                                    'relative text-sm font-medium transition-colors text-gray-900 dark:text-gray-200 hover:text-primary',
                                    item.disabled &&
                                        'cursor-not-allowed opacity-60',
                                    pathname === item.href && 'text-primary'
                                )}
                            >
                                {item.title}
                                {pathname === item.href && (
                                    <span className="absolute left-0 bottom-[-2px] h-[2px] w-full bg-primary rounded-full" />
                                )}
                            </Link>
                        ) : null
                    )}
                </nav>
            ) : null}

            {/* Mobile Navigation Menu */}
            {mobileMenuOpen && items?.length ? (
                <div className="absolute top-16 left-0 right-0 bg-white dark:bg-gray-900 shadow-md z-50 md:hidden">
                    <nav className="flex flex-col py-2">
                        {items.map((item, index) =>
                            item.href ? (
                                <Link
                                    key={index}
                                    href={item.href}
                                    className={cn(
                                        'px-4 py-3 text-sm font-medium transition-colors text-gray-900 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800',
                                        item.disabled &&
                                            'cursor-not-allowed opacity-60',
                                        pathname === item.href &&
                                            'text-primary bg-gray-50 dark:bg-gray-800'
                                    )}
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {item.title}
                                </Link>
                            ) : null
                        )}
                    </nav>
                </div>
            ) : null}
        </div>
    );
}
